import {Box, Button, Stack} from "@mui/material";
import {useCallback, useState} from "react";
import {resolveFieldComponent} from "./fieldRegistry";

/**
 * Serializes the current field values into a plain query-params object.
 * Empty strings, null and undefined are omitted. Uses `paramKey ?? field`
 * as the key so a field can map to a different API param name (e.g. snake_case).
 */
export const serialize = (fields, values) => {
  const params = {};
  fields.forEach((cfg) => {
    const value = values[cfg.field];
    if (value === undefined || value === null || value === "") return;
    params[cfg.paramKey ?? cfg.field] = value;
  });
  return params;
};

/**
 * Generic, declarative search bar. Renders a control per field config, manages
 * the values internally and composes the query-params object passed to
 * `onSearch`. Designed to plug into `usePaginationSearch` (`applyFilters` /
 * `resetFilters`) or any `onSearch(params)` consumer.
 */
const SearchBar = ({
  fields = [],
  initialValues = {},
  onSearch,
  onReset,
  searchLabel = "Buscar",
  resetLabel = "Limpiar",
  size = "small",
  components = {}
}) => {
  const [values, setValues] = useState(initialValues);

  const handleChange = useCallback((field, value) => {
    setValues((prev) => ({...prev, [field]: value}));
  }, []);

  const handleSearch = useCallback(() => {
    onSearch?.(serialize(fields, values));
  }, [fields, values, onSearch]);

  const handleReset = useCallback(() => {
    setValues({});
    if (onReset) onReset();
    else onSearch?.({});
  }, [onReset, onSearch]);

  return (
    <Box sx={{mb: 2}}>
      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" useFlexGap>
        {fields.map((cfg) => {
          const Field = resolveFieldComponent(cfg.type, components);
          return (
            <Field
              key={cfg.field}
              config={cfg}
              value={values[cfg.field]}
              size={size}
              onChange={(value) => handleChange(cfg.field, value)}
              onEnter={handleSearch}
            />
          );
        })}
        <Button variant="contained" size={size} onClick={handleSearch}>
          {searchLabel}
        </Button>
        <Button variant="outlined" size={size} onClick={handleReset}>
          {resetLabel}
        </Button>
      </Stack>
    </Box>
  );
};

export default SearchBar;
